import {Fragment, h} from 'preact';
import {Link} from 'preact-router/match';
import style from './header.module.css';

import LocationPicker from "./LocationPicker";
import AccountActions from "./AccountActions";
import SearchBar from "./SearchBar";
import MinicartButton from "./MinicartButton";

import {useHeader} from "./useHeader";

import {
    logo,
    iconMenu,
    iconLibras,
    iconDarkTheme
} from "../../../assets/";

import {SidebarCart} from "../../";
import SidebarMenu from "../../SidebarMenu";
import {useMediaQuery} from "../../../hooks";
import {useAppContext, useUserContext} from '../../../contexts';
import SearchBlock from "./SearchBlock";
import useBehavioralContext from "../../../contexts/BehavioralContext";
import SearchBackdrop from "./SearchBackdrop";

const Header = () => {
    // Component logic data
    const componentProps = useHeader();

    // Destructured component logic data
    const {
        hasScrolled,
        handleMenu,
        handleHandTalk,
        vendor
    } = componentProps;

    // Simple media query used to control the search bar component display
    const isTablet =
        useMediaQuery("(max-width: 768px)");

    // This is used to hide the second and the third header rows at the onboarding page
    const isOnboardingPage = window.location.pathname === "/onboarding";

    // Accessibility feature data
    const {
        accessibilityFont,
        handleAccessibilityFont,
        highContrast,
        handleHighContrast
    } = useAppContext();

    // Store logo image
    const [{}, {}, {}, {
        storeLogo,
    }] = useUserContext();

    // Retrieves the search bar action stuff
    const {searchBarData} = useBehavioralContext();

    // The Sabin store logo
    const logoSrc =
        typeof storeLogo !== "undefined" ? (storeLogo || logo) : "";

    return (
        <Fragment>
            <div className={`container-fluid relative ${highContrast ? "bg-black" : ""}`}>
                <div className={"sabin-container flex flex-row justify-between h-11"}>
                    {/* Left navigation */}
                    <nav className={"hidden md:flex flex-row gap-x-6"}>
                        <a 
                            className={`${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                            href="https://www.sabin.com.br/" 
                            target="_blank" rel="noopener noreferrer" 
                            data-testid="portal-sabin-link"
                            style={{fontSize: `${12+accessibilityFont}px`}}
                        >
                            <span>
                                Portal sabin
                            </span>
                        </a>
                        <a 
                            className={`${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                            href="https://laudos.sabin.com.br/shift/lis/sabin/elis/gestao/sabin.portal.login.csp" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            data-testid="results-link"
                            style={{fontSize: `${12+accessibilityFont}px`}}
                        >
                            <span>
                                Resultado de exames
                            </span>
                        </a>
                        <a 
                            className={`${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`} 
                            href="https://blog.sabin.com.br/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            data-testid="blog-link"
                            style={{fontSize: `${12+accessibilityFont}px`}}
                        >
                            <span>
                                Ir para o blog
                            </span>
                        </a>
                    </nav>

                    {/* Store picker pill */}
                    <LocationPicker />

                    {/* Right navigation */}
                    <nav className={"flex flex-row gap-x-4"}>
                        <button 
                            type={"button"} 
                            className={`!hidden ${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                            onClick={() => handleAccessibilityFont(accessibilityFont+1)}
                            disabled={accessibilityFont >= 2}
                            style={{fontSize: `${12+accessibilityFont}px`}}
                        >
                            <span className={`
                                    w-6 h-6 flex items-center justify-center leading-none rounded-full border select-none 
                                    ${accessibilityFont >= 2 
                                    ? `border-gray-400 text-gray-400 ${highContrast ? "border-white text-white" : ""}` 
                                    : `border-black ${highContrast ? "border-contrast-yellow text-contrast-yellow" : ""}`}`}>
                                A+
                            </span>
                        </button>
                        <button 
                            type={"button"} 
                            className={`!hidden ${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                            onClick={() => handleAccessibilityFont(accessibilityFont-1)}
                            disabled={accessibilityFont <= 0}
                            style={{fontSize: `${12+accessibilityFont}px`}}
                        >
                            <span className={`
                                    w-6 h-6 flex items-center justify-center leading-none rounded-full border select-none 
                                    ${accessibilityFont <= 0 
                                    ? `border-gray-400 text-gray-400 ${highContrast ? "text-white border-white" : ""}` 
                                    : `border-black ${highContrast ? "border-contrast-yellow text-contrast-yellow" : ""}`}`}>
                                A-
                            </span>
                        </button>
                        <button
                            type={"button"}
                            className={`w-6 h-6 select-none ${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                        >
                            <img
                                src={iconDarkTheme}
                                alt={"Dark mode icon"}
                                className={`w-full ${highContrast ? style.contrastYellowFilter : ''}`}
                                onClick={handleHighContrast}
                            />
                        </button>
                        <button 
                            type={"button"} 
                            className={`w-6 h-6 select-none ${style.navLink} text-black ${highContrast ? "highLabelFirst" : "hover:text-sabin-red-default"}`}
                            onClick={handleHandTalk}
                        >
                            <img
                                src={iconLibras}
                                alt={"Hand talk icon"}
                                className={`w-full ${highContrast ? style.handTalkYellowFilter : ''}`}
                            />
                        </button>
                    </nav>
                </div>
            </div>
            <div
                className={`
                    container-fluid border-y sticky top-0 
                    ${style.secondHeader} 
                    ${hasScrolled 
                        ? ` ${style.secondHeaderShadow}` 
                        : ""}
                    ${highContrast ? "grayBackground" : ""}
                    ${searchBarData["isOpen"] ? "!z-[15]" : ""} 
                    ${isOnboardingPage ? "!hidden" : ""}
                `}
                data-testid="header-container"
            >
                {/* The content for when the search bar is focused */}
                <SearchBlock vendor={vendor} />

                <div className={"sabin-container flex flex-row justify-between h-full gap-x-2 relative"}>
                    {/* Logo and store call */}
                    <div className={"h-full flex items-center gap-x-4"}>
                        {/* Mobile menu button */}
                        <button
                            className={`block md:hidden ${highContrast ? style.menuYellowFilter : ""}`}
                            onClick={() => handleMenu()}
                            data-testid="header-handler-menu-button"
                        >
                            <img
                                src={iconMenu}
                                alt={"Menu icon"}
                                className={`${style.menuIcon}`}
                            />
                        </button>

                        <Link
                            href="/"
                            data-testid="header-logo-link"
                            className={`${style.logoContainer} ${highContrast ? style.logoYellowFilter : ""}`}
                            onClick={() => {
                                window.location.pathname === "/" && window.location.reload();
                            }}
                        >
                            <img
                                src={logoSrc}
                                className={`${style.sabinLogo}`}
                                alt={"Sabin logo"}
                                data-testid="header-logo-img"
                            />
                        </Link>
                        {/*<p */}
                        {/*    className={`${style.storeCall} ${highContrast ? "highLabelFourth" : ""}`}*/}
                        {/*    style={{fontSize: `${16+accessibilityFont}px`}}*/}
                        {/*>*/}
                        {/*    |&nbsp;&nbsp;Loja virtual*/}
                        {/*</p>*/}
                    </div>

                    {/* Search bar */}
                    {!isTablet && (
                        <SearchBar />
                    )}

                    {/* Right menu */}
                    <div className={`h-full flex flex-row gap-x-5 relative z-[21]`}>
                        {/* Menu and account actions */}
                        <nav className={"h-full flex items-center gap-x-8 shrink-0"}>
                            {/* Account actions */}
                            <AccountActions />

                            {/* Desktop menu button */}
                            <button
                                className={`hidden md:block`}
                                onClick={() => handleMenu()}
                            >
                                <img
                                    src={iconMenu}
                                    alt={"Menu icon"}
                                    className={`${style.menuIcon}  ${highContrast ? "hover:!invert" : ""}`}
                                />
                            </button>
                        </nav>

                        {/* Minicart button */}
                        <MinicartButton />
                    </div>
                </div>
            </div>
            <div className={`container-fluid w-full bg-light-gray md:hidden ${highContrast ? "grayBackground" : ""} ${isOnboardingPage ? "!hidden" : ""}`}>
                <div className="sabin-container !py-2 relative">
                    {/* Search bar */}
                    {isTablet && (
                        <SearchBar />
                    )}
                </div>
            </div>

            {/* Search bar component backdrop */}
            <SearchBackdrop />

            {/* The sidebar menu component */}
            <SidebarMenu />

            {/* The sidebar cart component */}
            <SidebarCart />
        </Fragment>
    )
}

export default Header;
